// ================================================
// CLARITY REPORTING — PRIVACY POLICY PAGE
// ================================================

import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'May 13, 2026';
const COMPANY      = 'Clarity Reporting';
const DOMAIN       = 'clarityreporting.io';
const EMAIL        = 'support@clarityreporting.io';

export default function PrivacyPolicyPage() {
  return (
    <div style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1a1a1a', minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 60px', borderBottom: '1px solid #e0e0e0'
      }}>
        <Link to="/" style={{
          fontSize: 13, fontWeight: 700, letterSpacing: 3,
          textTransform: 'uppercase', textDecoration: 'none', color: '#1a1a1a'
        }}>
          Clarity<span style={{ fontWeight: 300, opacity: 0.5 }}> Reporting</span>
        </Link>
        <Link to="/login" style={{ fontSize: 13, color: '#888', textDecoration: 'none' }}>
          Sign In
        </Link>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '60px 40px 80px' }}>

        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: '#888', marginBottom: 16 }}>
          Legal
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 8, letterSpacing: -0.5 }}>
          Privacy Policy
        </h1>
        <p style={{ color: '#888', fontSize: 14, marginBottom: 48 }}>
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="1. Introduction">
          <p>
            {COMPANY} ("we," "us," or "our") operates the website {DOMAIN} and provides
            SEO competitive intelligence reporting services (the "Service"). This Privacy Policy
            explains how we collect, use, disclose, and safeguard your information when you use
            our Service.
          </p>
          <p style={{ marginTop: 12 }}>
            By using the Service, you agree to the collection and use of information in accordance
            with this policy. If you do not agree, please do not use our Service.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <SubHeading>Information You Provide</SubHeading>
          <ul>
            <li>Name and email address when you create an account</li>
            <li>Payment information — processed by Authorize.net or PayPal. We store only the last four digits of your card number. We never store full card numbers.</li>
            <li>Website URLs and competitor URLs you enter into the Service</li>
          </ul>

          <SubHeading>Information from Google Search Console (Optional)</SubHeading>
          <p>
            If you choose to connect your Google Search Console account, we collect OAuth access
            tokens and the keyword ranking data associated with your verified properties. This
            connection is optional and can be disconnected at any time from your account settings.
            We use this data exclusively to generate your reports.
          </p>

          <SubHeading>Information Collected Automatically</SubHeading>
          <ul>
            <li>Log data including IP address, browser type, pages visited, and timestamps</li>
            <li>Cookies and similar tracking technologies for session management</li>
          </ul>

          <SubHeading>SEO Data from Third-Party Sources</SubHeading>
          <p>
            We use DataForSEO to retrieve keyword ranking data for your website and competitor
            websites. This data is publicly available search engine result data.
          </p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul>
            <li>Generate and deliver your weekly SEO ranking reports</li>
            <li>Send transactional emails including account verification and password resets</li>
            <li>Process payments and manage your subscription</li>
            <li>Improve and maintain the Service</li>
            <li>Respond to your support requests</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            We do not sell, trade, or rent your personal information to third parties.
            We do not use your data for advertising purposes.
          </p>
        </Section>

        <Section title="4. Data Storage and Security">
          <p>
            Your data is stored on Amazon Web Services (AWS) infrastructure in the United States,
            including AWS RDS (database) and AWS S3 (report storage). We implement
            industry-standard security measures including:
          </p>
          <ul>
            <li>SSL/TLS encryption for all data in transit</li>
            <li>Encrypted database connections</li>
            <li>Bcrypt password hashing — we never store plain-text passwords</li>
            <li>AWS security groups restricting database access</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            No method of transmission over the Internet is 100% secure. While we strive to
            use commercially acceptable means to protect your information, we cannot guarantee
            absolute security.
          </p>
        </Section>

        <Section title="5. Data Retention">
          <p>
            We retain your account information for as long as your account is active. Report data
            is retained for 90 days in our storage system, after which older reports are
            automatically deleted. You may request deletion of your account and all associated
            data at any time by contacting us at {EMAIL}.
          </p>
        </Section>

        <Section title="6. Google API Services">
          <p>
            {COMPANY}'s use of information received from Google APIs adheres to the
            {' '}<a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#1a1a1a' }}>
              Google API Services User Data Policy
            </a>, including the Limited Use requirements.
          </p>
          <p style={{ marginTop: 12 }}>
            Specifically, we access Google Search Console data solely to generate SEO reports
            for you. We do not use this data for any other purpose, do not share it with
            third parties, and do not use it for advertising.
          </p>
        </Section>

        <Section title="7. Third-Party Services">
          <p>We use the following third-party services to operate the Service:</p>
          <ul>
            <li><strong>Amazon Web Services</strong> — cloud hosting, database, email delivery, and file storage</li>
            <li><strong>DataForSEO</strong> — SEO keyword and ranking data</li>
            <li><strong>Authorize.net</strong> — credit card payment processing</li>
            <li><strong>PayPal</strong> — alternative payment processing</li>
            <li><strong>Google</strong> — optional Search Console integration</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            Each of these services has their own privacy policy governing how they handle data.
          </p>
        </Section>

        <Section title="8. Your Rights">
          <p>You have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate personal data</li>
            <li>Request deletion of your personal data</li>
            <li>Disconnect your Google Search Console integration at any time</li>
            <li>Cancel your subscription at any time</li>
            <li>Export your report data</li>
          </ul>
          <p style={{ marginTop: 12 }}>
            To exercise any of these rights, contact us at {EMAIL}.
          </p>
        </Section>

        <Section title="9. Cookies">
          <p>
            We use essential cookies to maintain your login session. We do not use tracking
            cookies, advertising cookies, or third-party analytics cookies.
          </p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            Our Service is not directed to individuals under the age of 18. We do not knowingly
            collect personal information from children. If you believe a child has provided us
            with personal information, please contact us at {EMAIL}.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any
            changes by posting the new policy on this page and updating the "Last updated" date.
            Continued use of the Service after changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <div style={{
            marginTop: 16, padding: '20px 24px',
            background: '#f5f5f5', fontSize: 14, lineHeight: 1.8
          }}>
            <strong>{COMPANY}</strong><br />
            {DOMAIN}<br />
            <a href={`mailto:${EMAIL}`} style={{ color: '#1a1a1a' }}>{EMAIL}</a>
          </div>
        </Section>

      </div>

      {/* Footer */}
      <footer style={{ padding: '28px 60px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888' }}>
        <Link to="/" style={{ color: '#888', textDecoration: 'none', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', fontSize: 11 }}>Clarity Reporting</Link>
        <div style={{ display: 'flex', gap: 24 }}>
          <Link to="/privacy" style={{ color: '#888', textDecoration: 'none' }}>Privacy Policy</Link>
          <Link to="/terms" style={{ color: '#888', textDecoration: 'none' }}>Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{
        fontSize: 16, fontWeight: 700, marginBottom: 16,
        paddingBottom: 10, borderBottom: '1px solid #e0e0e0'
      }}>
        {title}
      </h2>
      <div style={{ fontSize: 14, lineHeight: 1.8, color: '#444' }}>
        {children}
      </div>
    </div>
  );
}

function SubHeading({ children }) {
  return (
    <p style={{ fontWeight: 700, fontSize: 14, marginTop: 16, marginBottom: 8, color: '#1a1a1a' }}>
      {children}
    </p>
  );
}
